import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LayoutGrid, Monitor, Tag, Palette, Video, LogOut, Tv, Menu, X } from 'lucide-react';
import { useStore } from '../../store/useStore';
import { useAuth } from '../../features/auth/hooks/useAuth';
import { authService } from '../../features/auth/services/authService';

const TopMenu = () => {
    const { showHeader } = useStore();
    const { logout } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [isAuthenticated, setIsAuthenticated] = useState(authService.isAuthenticated());
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    // Sincronizar estado de autenticação verificando o token periodicamente
    useEffect(() => {
        const checkAuth = () => {
            setIsAuthenticated(authService.isAuthenticated());
        };

        // Verificar imediatamente
        checkAuth();

        // Verificar periodicamente para sincronizar com mudanças (a cada 200ms)
        const interval = setInterval(checkAuth, 200);

        // Verificar quando a rota mudar
        const handleRouteChange = () => {
            checkAuth();
        };

        // Removido listener de 'storage' pois sessionStorage não dispara eventos entre abas/dispositivos
        // e pode causar problemas em produção
        
        return () => {
            clearInterval(interval);
        };
    }, [location.pathname]);

    const isDisplayPage = location.pathname.startsWith('/display');
    const isLoginPage = location.pathname === '/login';
    const isTelevisaoPage = location.pathname === '/televisao';

    const handleLogout = () => {
        logout();
        setIsAuthenticated(false);
        navigate('/login');
    };

    // Don't render menu on login page or televisao page
    if (isLoginPage || isTelevisaoPage) return null;

    // Don't render on display page unless showHeader is true
    if (isDisplayPage && !showHeader) return null;

    const isActive = (path: string) => {
        return location.pathname === path ? 'text-blue-600' : 'text-gray-900 hover:text-blue-600';
    };

    return (
        <nav className="sticky top-0 z-[2000] bg-white shadow-sm border-b">
            <div className="max-w-7xl mx-auto px-2 sm:px-4">
                <div className="flex justify-between items-center h-14 sm:h-16">
                    <div className="flex items-center">
                        <div className="flex-shrink-0 flex items-center">
                            <h1 className="text-lg sm:text-xl font-bold text-blue-600">Market Media</h1>
                        </div>
                        {/* Desktop Menu */}
                        <div className="hidden lg:ml-6 lg:flex lg:space-x-8">
                            <Link
                                to="/televisoes"
                                className={`inline-flex items-center px-1 pt-1 ${isActive('/televisoes')}`}
                            >
                                <Tv className="mr-2" size={20} />
                                Televisão
                            </Link>
                            <Link
                                to="/screens"
                                className={`inline-flex items-center px-1 pt-1 ${isActive('/screens')}`}
                            >
                                <LayoutGrid className="mr-2" size={20} />
                                Telas
                            </Link>
                            <Link
                                to="/videos"
                                className={`inline-flex items-center px-1 pt-1 ${isActive('/videos')}`}
                            >
                                <Video className="mr-2" size={20} />
                                Vídeo
                            </Link>
                            <Link
                                to="/promotions"
                                className={`inline-flex items-center px-1 pt-1 ${isActive('/promotions')}`}
                            >
                                <Tag className="mr-2" size={20} />
                                Produtos
                            </Link>
                            <Link
                                to="/styles"
                                className={`inline-flex items-center px-1 pt-1 ${isActive('/styles')}`}
                            >
                                <Palette className="mr-2" size={20} />
                                Estilo
                            </Link>
                            <Link
                                to="/display"
                                className={`inline-flex items-center px-1 pt-1 ${isDisplayPage ? 'text-blue-600' : 'text-gray-900 hover:text-blue-600'}`}
                            >
                                <Monitor className="mr-2" size={20} />
                                Visualização
                            </Link>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        {isAuthenticated && (
                            <>
                                {/* Mobile Menu Button */}
                                <button
                                    onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                                    className="lg:hidden flex items-center gap-1.5 px-2 py-2 text-gray-700 hover:text-blue-600 focus:outline-none"
                                >
                                    {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
                                    <span className="text-sm font-medium">Menu</span>
                                </button>
                                {/* Botão Sair - sempre visível e sempre no final (mobile e desktop) */}
                                <button
                                    onClick={handleLogout}
                                    className="inline-flex items-center px-2 sm:px-3 py-2 text-sm text-gray-700 hover:text-blue-600 whitespace-nowrap"
                                >
                                    <LogOut className="mr-1.5 sm:mr-2" size={18} />
                                    <span>Sair</span>
                                </button>
                            </>
                        )}
                    </div>
                </div>
                {/* Mobile Menu */}
                {mobileMenuOpen && (
                    <div className="lg:hidden border-t border-gray-200">
                        <div className="px-2 pt-2 pb-3 space-y-1">
                            <Link
                                to="/televisoes"
                                onClick={() => setMobileMenuOpen(false)}
                                className={`block px-3 py-2 rounded-md text-base font-medium ${isActive('/televisoes')} bg-gray-50`}
                            >
                                <div className="flex items-center">
                                    <Tv className="mr-2" size={18} />
                                    Televisão
                                </div>
                            </Link>
                            <Link
                                to="/screens"
                                onClick={() => setMobileMenuOpen(false)}
                                className={`block px-3 py-2 rounded-md text-base font-medium ${isActive('/screens')} bg-gray-50`}
                            >
                                <div className="flex items-center">
                                    <LayoutGrid className="mr-2" size={18} />
                                    Telas
                                </div>
                            </Link>
                            <Link
                                to="/videos"
                                onClick={() => setMobileMenuOpen(false)}
                                className={`block px-3 py-2 rounded-md text-base font-medium ${isActive('/videos')} bg-gray-50`}
                            >
                                <div className="flex items-center">
                                    <Video className="mr-2" size={18} />
                                    Vídeo
                                </div>
                            </Link>
                            <Link
                                to="/promotions"
                                onClick={() => setMobileMenuOpen(false)}
                                className={`block px-3 py-2 rounded-md text-base font-medium ${isActive('/promotions')} bg-gray-50`}
                            >
                                <div className="flex items-center">
                                    <Tag className="mr-2" size={18} />
                                    Produtos
                                </div>
                            </Link>
                            <Link
                                to="/styles"
                                onClick={() => setMobileMenuOpen(false)}
                                className={`block px-3 py-2 rounded-md text-base font-medium ${isActive('/styles')} bg-gray-50`}
                            >
                                <div className="flex items-center">
                                    <Palette className="mr-2" size={18} />
                                    Estilo
                                </div>
                            </Link>
                            <Link
                                to="/display"
                                onClick={() => setMobileMenuOpen(false)}
                                className={`block px-3 py-2 rounded-md text-base font-medium ${isDisplayPage ? 'text-blue-600 bg-blue-50' : 'text-gray-900 hover:bg-gray-50'}`}
                            >
                                <div className="flex items-center">
                                    <Monitor className="mr-2" size={18} />
                                    Visualização
                                </div>
                            </Link>
                        </div>
                    </div>
                )}
            </div>
        </nav>
    );
};

export default TopMenu;
